import React from "react";
import Slider from "@mui/material/Slider";

interface ClassificationSliderProps {
  histogramData: { min: number; max: number };
  sliderValues: number[];
  setSliderValues: (newValues: number[]) => void;
  isFlipped: boolean;
  handleReverse: () => void;
}

const ClassificationSlider: React.FC<ClassificationSliderProps> = ({ 
  histogramData, 
  sliderValues, 
  setSliderValues, 
  isFlipped, 
  handleReverse 
}) => {

    // Determine category labels based on flip state
    const categoryLabels = isFlipped 
    ? ["Low", "Medium", "High"] 
    : ["High", "Medium", "Low"];

  // Handle Slider Change
  const handleSliderChange = (event: Event | React.SyntheticEvent, value: number | number[], activeThumb: number) => {
    if (Array.isArray(value)) {
      setSliderValues(value);
    }
  };

  // Calculate percentage positions for the markers based on slider values
  const calculatePercentage = (value: number) => {
    const range = histogramData.max - histogramData.min;
    return ((value - histogramData.min) / range) * 100;
  };


  const leftSliderPos = calculatePercentage(sliderValues[0]);
  const rightSliderPos = calculatePercentage(sliderValues[1]);

  // Determine which labels to show based on slider positions
  const showLeftLabel = leftSliderPos > 0.01; // Hide if slider at minimum
  const showMiddleLabel = Math.abs(leftSliderPos - rightSliderPos) > 0.01; // Hide if sliders are equal
  const showRightLabel = rightSliderPos < 99.99; // Hide if slider at maximum



  // Calculate column widths
  const column1Width = leftSliderPos;
  const column2Width = rightSliderPos - leftSliderPos;
  const column3Width = 100 - rightSliderPos;

  // Minimum width (in percentage) for a column to display text in-line
  const MIN_WIDTH_FOR_INLINE = 15;
  
  // Determine if columns are too narrow for text
  const isColumn1Narrow = column1Width < MIN_WIDTH_FOR_INLINE;
  const isColumn2Narrow = column2Width < MIN_WIDTH_FOR_INLINE;
  const isColumn3Narrow = column3Width < MIN_WIDTH_FOR_INLINE;

  // Get the labels that need to be positioned below (only for columns that should show labels)
  const labelsBelowColumns = [];
  if (isColumn1Narrow && showLeftLabel) labelsBelowColumns.push({ index: 0, label: categoryLabels[0], position: 0 });
  if (isColumn2Narrow && showMiddleLabel) labelsBelowColumns.push({ index: 1, label: categoryLabels[1], position: 1 });
  if (isColumn3Narrow && showRightLabel) labelsBelowColumns.push({ index: 2, label: categoryLabels[2], position: 2 });

  // Sort labels based on their horizontal position to avoid overlaps
  const sortedLabelsBelowColumns = [...labelsBelowColumns].sort((a, b) => {
    // Get center positions of columns
    const positions = [
      column1Width / 2,
      leftSliderPos + (column2Width / 2),
      rightSliderPos + (column3Width / 2)
    ];
    return positions[a.index] - positions[b.index];
  });

  // Calculate positions with minimum distances to avoid overlaps
  const calculateNonOverlappingPositions = (labels: string | any[]) => {
    if (labels.length === 0) return [];

    /*
    // Minimum distance between labels (in percentage)
    const MIN_DISTANCE = 20;
    
    // Get original center positions of columns
    const originalPositions = [
      column1Width / 2,
      leftSliderPos + (column2Width / 2),
      rightSliderPos + (column3Width / 2)
    ];
    
    // Calculate non-overlapping positions
    const positions = [];
    
    for (let i = 0; i < labels.length; i++) {
      const labelIndex = labels[i].index;
      let position = originalPositions[labelIndex];
      
      // Check if this position would overlap with previous positions
      for (let j = 0; j < i; j++) {
        const prevPosition = positions[j];
        if (Math.abs(position - prevPosition) < MIN_DISTANCE) {
          // If overlap detected, move this label to the right
          if (position > prevPosition) {
            position = prevPosition + MIN_DISTANCE;
          } else {
            position = prevPosition - MIN_DISTANCE;
          }
        }
      }
      
      // Ensure position is within bounds
      position = Math.max(MIN_DISTANCE / 2, Math.min(100 - MIN_DISTANCE / 2, position));
      
      positions.push(position);
    }
    */

    const general_label_length = 33 //px
    
    const positions : string[] = [
      `calc(${column1Width / 2}%)`,
      `calc(${leftSliderPos + column2Width / 2}%)`,
      `calc(${rightSliderPos + column3Width / 2}%)`
    ];

    if(isColumn1Narrow && showLeftLabel) {
      positions[0]=(`calc(${column1Width/2}%)`);

      if(isColumn2Narrow && showLeftLabel) {
        positions[1]=(`calc(${leftSliderPos+column2Width/2}% + ${general_label_length}px)`);
      }

    } else if(isColumn3Narrow && showRightLabel){

      if(isColumn2Narrow && showMiddleLabel) {
        positions[1]=(`calc(${leftSliderPos+column2Width/2}% - ${general_label_length}px)`);
      }
      positions[2]=`calc(${rightSliderPos + column3Width/2}%)`;
    } else { // just Medium is over
      if(isColumn2Narrow) // just middle is narrow
        positions[1]=(`calc(${leftSliderPos+column2Width/2}%)`); 
      if(leftSliderPos < 50)
        positions[1]=(`calc(${leftSliderPos+column2Width/2}% + ${general_label_length/2}px)`); 
      else 
        positions[1]=(`calc(${leftSliderPos+column2Width/2}% - ${general_label_length/2}px)`); 
    }

    if(!isColumn1Narrow) delete positions[0];
    if(!isColumn2Narrow) delete positions[1];
    if(!isColumn3Narrow) delete positions[2];

    return positions.filter(item => item);
  };

  const nonOverlappingPositions = calculateNonOverlappingPositions(sortedLabelsBelowColumns);

  return (
    <div>
      <h5 style={{color:"#1976d2", marginTop:"20px"}}>Classification Slider</h5>
      
      <p style={{color:"#1976d2", fontSize:"12px", textAlign:"center", marginTop:"10px"}}>
        Current Breakpoints: {sliderValues.map(v => v.toExponential(3)).join(" | ")}
      </p>

      {/* Slider */}
      <Slider
         style={{marginTop:"10px"}}
        value={sliderValues}
        onChange={handleSliderChange}
        step={(histogramData.max - histogramData.min) / 100 || 0.01}
        min={histogramData.min}
        max={histogramData.max}
        valueLabelDisplay="auto"
      />

      {/* Dynamic markers and labels */}
      <div className="dynamic-classification-container">
        {/* Vertical markers at fixed positions */}
        <div className="marker-line" style={{ left: '0%' }}></div>
        <div className="marker-line" style={{ left: `${leftSliderPos}%` }}></div>
        <div className="marker-line" style={{ left: `${rightSliderPos}%` }}></div>
        <div className="marker-line" style={{ left: '100%' }}></div>
        
        {/* Labels in their columns - only show if wide enough AND should be visible */}
        {!isColumn1Narrow && showLeftLabel && (
          <div className="classification-label" style={{ 
            left: '0%', 
            width: `${leftSliderPos}%`,
            color: '#1976d2'
          }}>
            {categoryLabels[0]}
          </div>
        )}
        
        {!isColumn2Narrow && showMiddleLabel && (
          <div className="classification-label" style={{ 
            left: `${leftSliderPos}%`, 
            width: `${column2Width}%`,
            color: '#1976d2'
          }}>
            {categoryLabels[1]}
          </div>
        )}
        
        {!isColumn3Narrow && showRightLabel && (
          <div className="classification-label" style={{ 
            left: `${rightSliderPos}%`, 
            width: `${column3Width}%`,
            color: '#1976d2'
          }}>
            {categoryLabels[2]}
          </div>
        )}
      </div>

      {/* Labels shown below for narrow columns - with non-overlapping positions */}
      {sortedLabelsBelowColumns.length > 0 && (
        <div className="labels-below-container">
          {sortedLabelsBelowColumns.map((item, idx) => (
            <div 
              key={item.index} 
              className="label-below" 
              style={{
                left: `${nonOverlappingPositions[idx]}`,
                color: '#1976d2'
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}

      {/* Reverse Button */}
      <button onClick={handleReverse} className="blue-button">
        Reverse Categories
      </button>
    </div>
  );
};

export default ClassificationSlider;